/**
 * Bypass Manager - Manages subscription bypass status in localStorage
 *
 * This is used by the axios interceptor (outside of React context) to determine
 * if the current user has a subscription bypass (internal/partner accounts).
 *
 * The bypass status is synced from the auth context when the user is loaded.
 */
class BypassManager {
  private readonly BYPASS_KEY = 'fl_subscription_bypass';
  private readonly ACCOUNT_TYPE_KEY = 'fl_account_type';
  private readonly FREE_PLAN_KEY = 'fl_free_plan';

  /**
   * Set the bypass status in localStorage
   * Called from auth-context when user data is loaded
   */
  setBypassStatus(hasSubscriptionBypass: boolean): void {
    localStorage.setItem(this.BYPASS_KEY, JSON.stringify(hasSubscriptionBypass));
  }

  /**
   * Check if the current user has subscription bypass
   * Used by axios interceptor to skip paywall redirects
   */
  hasSubscriptionBypass(): boolean {
    try {
      const value = localStorage.getItem(this.BYPASS_KEY);
      return value ? JSON.parse(value) === true : false;
    } catch {
      return false;
    }
  }

  /**
   * Set the account type in localStorage
   * Called from auth-context when user data is loaded
   */
  setAccountType(accountType: string | undefined): void {
    if (accountType) {
      localStorage.setItem(this.ACCOUNT_TYPE_KEY, accountType);
    } else {
      localStorage.removeItem(this.ACCOUNT_TYPE_KEY);
    }
  }

  /**
   * Get the account type from localStorage
   * Used by axios interceptor to determine bypass behavior
   */
  getAccountType(): string | null {
    return localStorage.getItem(this.ACCOUNT_TYPE_KEY);
  }

  /**
   * Set whether the organization is on the free plan
   * Called from auth-context when user data is loaded
   */
  setFreePlan(isFreePlan: boolean): void {
    localStorage.setItem(this.FREE_PLAN_KEY, JSON.stringify(isFreePlan));
  }

  /**
   * Whether the organization is on the free plan
   * Used by the axios interceptor, which never sends a free organization to the paywall
   */
  isOnFreePlan(): boolean {
    try {
      const value = localStorage.getItem(this.FREE_PLAN_KEY);
      return value ? JSON.parse(value) === true : false;
    } catch {
      return false;
    }
  }

  /**
   * Clear the bypass status, account type and free plan from localStorage
   * Called on logout or authentication errors
   */
  clearBypassStatus(): void {
    localStorage.removeItem(this.BYPASS_KEY);
    localStorage.removeItem(this.ACCOUNT_TYPE_KEY);
    localStorage.removeItem(this.FREE_PLAN_KEY);
  }
}

export const bypassManager = new BypassManager();
